import {
    formattedDollarsToCents,
    roundCentsToWholeDollarCents,
} from '@fruitful/fruitils';
import {
    calculateMonthlyTakeHomeCents,
    type MembershipIncome,
} from '@fruitful/member-profile';
import type {
    DeepPartial,
    MoneyMapWithRulesAndAccounts,
} from '@fruitful/types';
import { useCallback, useEffect, useState } from 'react';
import { useToastController } from 'smoothie';

import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../../common/components/Toasts';
import { captureException } from '../../../logger/Logger';
import { getMoneyMapperEffectiveMonthlyIncomeCents } from '../../utils/moneyMapperEffectiveIncome';
import { centsToDollarInputDisplay } from './moneyMapperIncomeForm';

export const useMoneyMapperGuideMonthlyTakeHome = ({
    moneyMap,
    membershipIncomes,
    persistMonthlyIncomeCentsToMoneyMap,
}: {
    moneyMap: DeepPartial<MoneyMapWithRulesAndAccounts>;
    membershipIncomes: MembershipIncome[];
    persistMonthlyIncomeCentsToMoneyMap: (
        monthlyIncomeCents: number,
    ) => Promise<boolean>;
}) => {
    const toast = useToastController();

    const [isEditingGuideTotal, setIsEditingGuideTotal] = useState(false);
    const [guideDraftStr, setGuideDraftStr] = useState('');
    const [isSavingGuide, setIsSavingGuide] = useState(false);

    const effectiveMonthlyCents = getMoneyMapperEffectiveMonthlyIncomeCents({
        moneyMap,
        membershipIncomes,
    });

    const sourcesTotalCents = calculateMonthlyTakeHomeCents(membershipIncomes);

    const mapMonthlyCents = moneyMap.monthlyIncomeCents ?? 0;
    const manualDiffersFromSourcesTotal =
        sourcesTotalCents > 0 &&
        roundCentsToWholeDollarCents(mapMonthlyCents) !==
            roundCentsToWholeDollarCents(sourcesTotalCents);

    const showActualTakeHomeSubline =
        sourcesTotalCents > 0 &&
        roundCentsToWholeDollarCents(effectiveMonthlyCents) !==
            roundCentsToWholeDollarCents(sourcesTotalCents);

    useEffect(() => {
        if (!isEditingGuideTotal) {
            setGuideDraftStr(centsToDollarInputDisplay(effectiveMonthlyCents));
        }
    }, [effectiveMonthlyCents, isEditingGuideTotal]);

    const removeManualGuideTotal = useCallback(async () => {
        setIsSavingGuide(true);
        try {
            const nextMonthly = roundCentsToWholeDollarCents(sourcesTotalCents);
            const ok = await persistMonthlyIncomeCentsToMoneyMap(nextMonthly);
            if (!ok) {
                return;
            }
            setIsEditingGuideTotal(false);
            toast.show(
                'Using total from income sources',
                MutationSuccessToastOptions,
            );
        } catch (error) {
            captureException(error);
            toast.show('Error updating', MutationErrorToastOptions(error));
        } finally {
            setIsSavingGuide(false);
        }
    }, [persistMonthlyIncomeCentsToMoneyMap, sourcesTotalCents, toast]);

    const beginGuideInputEdit = useCallback(() => {
        setIsEditingGuideTotal(true);
        setGuideDraftStr(centsToDollarInputDisplay(effectiveMonthlyCents));
    }, [effectiveMonthlyCents]);

    const cancelGuideInputEdit = useCallback(() => {
        setIsEditingGuideTotal(false);
        setGuideDraftStr(centsToDollarInputDisplay(effectiveMonthlyCents));
    }, [effectiveMonthlyCents]);

    const commitGuideTotal = useCallback(async () => {
        const trimmed = guideDraftStr.trim();

        setIsSavingGuide(true);
        try {
            if (trimmed.length === 0) {
                toast.show(
                    'Enter a valid amount or cancel.',
                    MutationErrorToastOptions(),
                );
                return;
            }

            const parsed = roundCentsToWholeDollarCents(
                formattedDollarsToCents(guideDraftStr),
            );
            if (Number.isNaN(parsed) || parsed < 0) {
                toast.show(
                    'Enter a valid amount.',
                    MutationErrorToastOptions(),
                );
                return;
            }

            const ok = await persistMonthlyIncomeCentsToMoneyMap(parsed);
            if (!ok) {
                return;
            }
            setIsEditingGuideTotal(false);
            toast.show(
                'Monthly take-home updated',
                MutationSuccessToastOptions,
            );
        } catch (error) {
            captureException(error);
            toast.show('Error saving', MutationErrorToastOptions(error));
        } finally {
            setIsSavingGuide(false);
        }
    }, [guideDraftStr, persistMonthlyIncomeCentsToMoneyMap, toast]);

    return {
        effectiveMonthlyCents,
        sourcesTotalCents,
        manualDiffersFromSourcesTotal,
        showActualTakeHomeSubline,
        isEditingGuideTotal,
        guideDraftStr,
        setGuideDraftStr,
        isSavingGuide,
        commitGuideTotal,
        removeManualGuideTotal,
        beginGuideInputEdit,
        cancelGuideInputEdit,
    };
};

export type MoneyMapperGuideMonthlyTakeHome = ReturnType<
    typeof useMoneyMapperGuideMonthlyTakeHome
>;
